<script setup lang="ts">
/**
 * The agent's questions, one per page, in a full-height sheet on a phone: swipe or step between them (the dots
 * up top say where you are), tap 52px rows to pick (checkmarks for one, ticks for several), type into
 * "Something else" or a typed answer above the keyboard, and Back / Next (Skip when nothing's picked yet, Send
 * on the last) in a footer that rides above the home indicator and the keyboard. Answers live in the shared
 * ask form, so closing the sheet midway keeps them.
 */
import { computed, ref, watch } from 'vue'
import DetentSheet from '@/components/mobile/DetentSheet.vue'
import SnapPager from '@/components/mobile/SnapPager.vue'
import { haptic } from '@/native/haptics'
import { vPress } from '@/directives/press'
import type { AskForm } from '../askForm'

const props = defineProps<{ open: boolean; form: AskForm; question: string; disabled?: boolean }>()
const emit = defineEmits<{ close: []; send: [text: string] }>()

/** iOS grouped look: white rows on the canvas. */
const SHEET_STYLE = { '--ds-bg': 'var(--agent-canvas, #fafafa)' }

const page = ref(0)
const fields = computed(() => props.form.fields.value)
const total = computed(() => fields.value.length)
const isLast = computed(() => page.value >= total.value - 1)
const ready = computed(() => props.form.ready(page.value))
const nextLabel = computed(() => (isLast.value ? 'Send' : ready.value ? 'Next' : 'Skip'))

watch(() => props.open, (o) => { if (o) page.value = firstUnanswered() })
function firstUnanswered(): number {
    const at = fields.value.findIndex((_, fi) => !props.form.ready(fi) && !props.form.skipped[fi])
    return at < 0 ? 0 : at
}

function pick(fi: number, i: number) {
    if (props.disabled) return
    props.form.pick(fi, i)
    haptic(fields.value[fi].multi ? 'toggle' : 'selection')
}
function pickOther(fi: number, el: HTMLElement) {
    if (!fields.value[fi].multi) pick(fi, props.form.otherIndex(fi))
    el.querySelector('input')?.focus()
}
function next() {
    if (props.disabled) return
    if (!ready.value) props.form.skipField(page.value)
    if (!isLast.value) {
        page.value++
        return
    }
    emit('send', props.form.message.value)
}
function back() {
    if (page.value > 0) page.value--
    else emit('close')
}
const hint = (fi: number) => {
    const f = fields.value[fi]
    return [f.multi ? 'Pick any' : f.options.length ? 'Pick one' : 'Type your answer', f.optional ? 'Optional' : ''].filter(Boolean).join(' · ')
}
</script>

<template>
    <DetentSheet
        :open="open"
        :detents="['large']"
        aria-label="Answer the questions"
        :style="SHEET_STYLE"
        data-testid="question-sheet"
        @close="emit('close')"
    >
        <template #header>
            <div class="qsh-head">
                <div class="min-w-0 flex-1">
                    <p class="qsh-count">{{ total > 1 ? `Question ${page + 1} of ${total}` : 'Your answer' }}</p>
                    <div v-if="total > 1" class="qsh-dots" role="tablist" aria-label="Questions">
                        <button
                            v-for="(f, fi) in fields"
                            :key="fi"
                            type="button"
                            role="tab"
                            class="qsh-dot"
                            :class="{ 'is-on': fi === page, 'is-done': form.valuesFor(fi).length > 0 }"
                            :aria-selected="fi === page"
                            :aria-label="`Question ${fi + 1}${form.valuesFor(fi).length ? ', answered' : ''}`"
                            @click="page = fi"
                        ><span /></button>
                    </div>
                </div>
                <button v-press.icon type="button" class="qsh-close" aria-label="Close" @click="emit('close')">
                    <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4.5 4.5l7 7M11.5 4.5l-7 7" /></svg>
                </button>
            </div>
        </template>

        <SnapPager v-model="page" :count="total" :gap="16" aria-label="Questions" class="qsh-pager">
            <template #default="{ index: fi }">
                <div class="qsh-page" data-testid="question-page">
                    <h3 class="qsh-q">{{ fields[fi].question || question }}</h3>
                    <p class="qsh-hint">{{ hint(fi) }}</p>

                    <ul v-if="fields[fi].options.length" class="qsh-rows" :role="fields[fi].multi ? 'group' : 'radiogroup'" :aria-label="fields[fi].question || question">
                        <li v-for="(o, i) in fields[fi].options" :key="o">
                            <button
                                v-press.row
                                type="button"
                                class="qsh-row"
                                :class="{ 'is-on': form.isOn(fi, i), 'is-multi': fields[fi].multi }"
                                :role="fields[fi].multi ? 'checkbox' : 'radio'"
                                :aria-checked="form.isOn(fi, i)"
                                :disabled="disabled"
                                @click="pick(fi, i)"
                            >
                                <span class="qsh-row-text">{{ o }}</span>
                                <span class="qsh-mark" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="M3.5 8.5l3 3 6-6.5" /></svg></span>
                            </button>
                        </li>
                        <li>
                            <label
                                class="qsh-row is-other"
                                :class="{ 'is-on': form.isOn(fi, form.otherIndex(fi)), 'is-multi': fields[fi].multi }"
                                @click="pickOther(fi, $event.currentTarget as HTMLElement)"
                            >
                                <input
                                    :value="form.custom[fi]"
                                    class="qsh-input"
                                    type="text"
                                    placeholder="Something else…"
                                    aria-label="Something else"
                                    enterkeyhint="next"
                                    :disabled="disabled"
                                    @input="form.setCustom(fi, ($event.target as HTMLInputElement).value)"
                                    @keydown.enter.prevent="next"
                                >
                                <span class="qsh-mark" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="M3.5 8.5l3 3 6-6.5" /></svg></span>
                            </label>
                        </li>
                    </ul>
                    <textarea
                        v-else
                        :value="form.custom[fi]"
                        class="qsh-text"
                        rows="3"
                        placeholder="Your answer"
                        :aria-label="fields[fi].question || question"
                        :disabled="disabled"
                        @input="form.setCustom(fi, ($event.target as HTMLTextAreaElement).value)"
                    />
                </div>
            </template>
        </SnapPager>

        <template #footer>
            <div class="qsh-foot">
                <button v-press.pill type="button" class="qsh-btn is-soft" :disabled="disabled" data-testid="question-back" @click="back">
                    {{ page > 0 ? 'Back' : 'Cancel' }}
                </button>
                <button
                    v-press.pill
                    type="button"
                    class="qsh-btn"
                    :class="nextLabel === 'Skip' ? 'is-soft' : 'is-primary'"
                    :disabled="disabled"
                    data-testid="question-next"
                    @click="next"
                >
                    {{ nextLabel }}
                    <svg v-if="nextLabel === 'Send'" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 12.5V3.8M4.2 7.4L8 3.6l3.8 3.8" /></svg>
                </button>
            </div>
        </template>
    </DetentSheet>
</template>

<style scoped>
.qsh-head { display: flex; align-items: flex-start; gap: 12px; padding: 2px 16px 10px 20px; }
.qsh-count { font-size: 13px; font-weight: 600; color: var(--agent-text-3); }
.qsh-dots { display: flex; gap: 0; margin: -2px 0 -6px -14px; }
/* 36px hit areas (the dots themselves stay 8px). */
.qsh-dot {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    -webkit-tap-highlight-color: transparent;
}
.qsh-dot span {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: rgb(15 15 15 / 0.16);
    transition: background-color var(--lh-dur-fast, 150ms) ease, transform var(--lh-dur-fast, 150ms) var(--lh-spring-soft, ease);
}
html.dark .qsh-dot:not(.is-on):not(.is-done) span { background: rgb(255 255 255 / 0.22); }
.qsh-dot.is-done span { background: rgb(251 76 31 / 0.4); }
.qsh-dot.is-on span { background: var(--agent-accent); transform: scale(1.2); }
.qsh-close {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 999px;
    color: var(--agent-text-2);
    background: var(--agent-hover);
}
.qsh-close svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; }

.qsh-page { padding: 4px 20px 24px; }
.qsh-q {
    font-size: 21px;
    font-weight: 650;
    line-height: 1.3;
    letter-spacing: -0.01em;
    color: var(--agent-text-1);
    text-wrap: pretty;
}
.qsh-hint { margin-top: 4px; font-size: 13.5px; color: var(--agent-text-3); }

.qsh-rows {
    margin: 16px 0 0;
    padding: 0;
    list-style: none;
    overflow: hidden;
    border-radius: 16px;
    background: var(--agent-card);
    box-shadow: 0 0 0 1px var(--agent-card-border);
}
.qsh-rows li + li { box-shadow: inset 0 1px 0 var(--agent-card-border); }
.qsh-row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 52px;
    padding: 12px 16px;
    text-align: left;
    font-size: 16px;
    line-height: 1.35;
    color: var(--agent-text-1);
    -webkit-tap-highlight-color: transparent;
}
.qsh-row:disabled { opacity: 0.6; }
.qsh-row-text { flex: 1; min-width: 0; overflow-wrap: anywhere; }
.qsh-row.is-on .qsh-row-text { font-weight: 600; }
.qsh-mark {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 999px;
    color: var(--agent-accent);
    transition: background-color 160ms ease, box-shadow 160ms ease, transform 240ms var(--lh-spring-pop, ease);
}
.qsh-mark svg {
    width: 16px;
    height: 16px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 16;
    stroke-dashoffset: 16;
    transition: stroke-dashoffset 240ms var(--lh-ease-out, ease);
}
.qsh-row.is-on .qsh-mark svg { stroke-dashoffset: 0; }
/* Several can apply: a round tick box, filled when on. */
.qsh-row.is-multi .qsh-mark { color: #fff; box-shadow: inset 0 0 0 1.5px var(--agent-text-4); }
.qsh-row.is-multi.is-on .qsh-mark { background: var(--agent-accent); box-shadow: none; transform: scale(1.04); }
.qsh-row.is-multi .qsh-mark svg { width: 13px; height: 13px; }
.is-other { cursor: text; }
.qsh-input {
    flex: 1;
    min-width: 0;
    padding: 0;
    border: 0;
    outline: none;
    background: transparent;
    font: inherit;
    font-size: 16px;
    color: var(--agent-text-1);
    box-shadow: none !important;
}
.qsh-input::placeholder, .qsh-text::placeholder { color: var(--agent-text-4); }
.qsh-text {
    display: block;
    width: 100%;
    margin-top: 16px;
    padding: 14px 16px;
    border: 0;
    border-radius: 16px;
    outline: none;
    resize: none;
    font: inherit;
    font-size: 16px;
    line-height: 1.4;
    color: var(--agent-text-1);
    background: var(--agent-card);
    box-shadow: 0 0 0 1px var(--agent-card-border);
}
.qsh-text:focus { box-shadow: 0 0 0 2px var(--agent-accent-ring); }

.qsh-foot {
    display: flex;
    gap: 10px;
    padding: 10px 16px;
    border-top: 1px solid var(--agent-card-border);
}
.qsh-btn {
    display: inline-flex;
    flex: 1 1 0;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-height: 50px;
    border-radius: 14px;
    font-size: 16px;
    font-weight: 600;
    -webkit-tap-highlight-color: transparent;
}
.qsh-btn:disabled { opacity: 0.5; }
.qsh-btn.is-soft { color: var(--agent-text-1); background: var(--agent-press); }
.qsh-btn.is-primary {
    color: #fff;
    background: linear-gradient(180deg, var(--agent-accent-2) 0%, var(--agent-accent) 100%);
    box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.35), 0 6px 16px -6px rgb(251 76 31 / 0.6);
}
.qsh-btn svg { width: 15px; height: 15px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
</style>
